import { useState } from "react";
import { matchPath } from "react-router-dom";
import { wayInPending } from "../../features/auth/services/signupArrival";
import { ROUTES } from "../../routes/paths";

/**
 * Which screens inside the shell are steps of a practice's way in, and so
 * should have none of the app drawn around them.
 *
 * The free trial's three screens are outside `AppLayout` altogether and are
 * not here: the offer, its detail step and the confirmation it ends on draw
 * their own shells and there is nothing to turn off. The paid branch of the
 * same offer is the odd one out. Somebody who buys a term outright rather
 * than taking the fortnight is sent to the *ordinary* checkout — the one the
 * Billings tab opens — and to its result screen after it, and both of those
 * are screens of the app, inside the rail and the tab bar.
 *
 * Which is what the reader saw: the offer is a white page with no furniture
 * on it, and pressing Buy on a term put the rail or the floating bar on the
 * screen with nothing in the page behind it while three calls answered. The
 * app appeared, for about a second, in the middle of buying the thing that
 * leads to it — and then the checkout arrived and the whole page redrew. See
 * `AppLayout`, which draws neither for these two paths.
 */
const WAY_IN_PATHS = [ROUTES.billingCheckout, ROUTES.billingResult];

/**
 * Whether a path is one of them.
 *
 * Matched exactly, which is why both are listed: `/billing/:cycle` does not
 * answer for `/billing/:cycle/result`, and the result screen is as much a step
 * of the way in as the checkout before it.
 */
export const onWayInPath = (pathname: string): boolean =>
  WAY_IN_PATHS.some((pattern) => matchPath(pattern, pathname) !== null);

/** The mark the offer puts on its own navigation into the paid checkout. */
const WAY_IN_STATE = "wayIn";

/**
 * What the offer passes when it sends somebody to buy a term:
 * `navigate(billingCheckoutPath(plan.id), { state: wayInState() })`, and the
 * same again on each step after it.
 *
 * Carried on the navigation rather than worked out from the session, because
 * the session's own answer is not the whole of it. `signupArrival` knows a
 * visit that signed up — the consent screen is where it is recorded — and that
 * covers the common case and not every case: somebody who left the offer
 * yesterday and signed in again this morning is sent straight back to it, with
 * no sign-up on this visit to be found, and buying a term from there is the
 * same journey through the same three screens. What they have in common is how
 * they arrived, which is what this says.
 *
 * It rides in the history entry, so it survives a reload of the step it is on
 * — which is the whole reason it is a navigation state and not a flag in
 * memory.
 */
export const wayInState = () => ({ [WAY_IN_STATE]: true });

/** Whether a history entry carries it. */
export const fromWayIn = (state: unknown): boolean =>
  (state as { [WAY_IN_STATE]?: unknown } | null)?.[WAY_IN_STATE] === true;

/**
 * Whether the shell is standing in for the last steps of the way in rather
 * than holding a screen of the app.
 *
 * Two halves, and they are read at different times on purpose.
 *
 * *Whether this reader has arrived in the app yet* is latched when the shell
 * mounts, not read per render. `markWayInFinished` is called by the result
 * screen itself — it is the end of the way in — so a value read fresh would
 * flip under the very screen that flipped it, and the rail would appear over a
 * confirmation the moment anything re-rendered. Latching answers for the whole
 * of the shell's life, which is exactly the journey being described.
 *
 * *Which screen is on show* is read every render, because that is what ends
 * it: the way out of both confirmations is the dashboard, and the dashboard is
 * where the rail appears for the first time. It is not on the list, so it gets
 * the shell it always had without this having to be told the journey is over.
 *
 * An established practice buying another term opens the checkout from the
 * Billings tab, under a shell that mounted on the dashboard long before, and
 * answers false on every count — it keeps the rail, the bar and the menu it
 * has had all along.
 */
export function useWayInShell(pathname: string, state: unknown): boolean {
  const [arrivingStill] = useState(() => wayInPending() || fromWayIn(state));
  return arrivingStill && onWayInPath(pathname);
}

export default useWayInShell;
